import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { getToken, setToken, clearToken } from '../../services/tokenStorage'

export interface CustomerProfile {
  id: number
  name: string
  email: string | null
  phone: string | null
  avatar: string | null
  /** Present on /me/profile and on sign-in; the profile form edits these directly. */
  first_name?: string
  last_name?: string
  gender?: string | null
  /** `yyyy-MM-dd`. */
  date_of_birth?: string | null
}

interface AuthState {
  customer: CustomerProfile | null
  /** Mirrors localStorage so components re-render on sign in/out. */
  isAuthenticated: boolean
}

const initialState: AuthState = { customer: null, isAuthenticated: false }

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signedIn(
      state,
      action: PayloadAction<{
        slug: string
        token: string
        expiresIn?: number
        customer: CustomerProfile
      }>,
    ) {
      setToken(action.payload.slug, action.payload.token, action.payload.expiresIn)
      state.customer = action.payload.customer
      state.isAuthenticated = true
    },
    profileLoaded(state, action: PayloadAction<CustomerProfile>) {
      state.customer = action.payload
      state.isAuthenticated = true
    },
    signedOut(state, action: PayloadAction<string>) {
      clearToken(action.payload)
      state.customer = null
      state.isAuthenticated = false
    },
    /** Called once on boot: a stored, unexpired token means "probably signed in". */
    restoreSession(state, action: PayloadAction<string>) {
      state.isAuthenticated = Boolean(getToken(action.payload))
    },
  },
})

export const { signedIn, profileLoaded, signedOut, restoreSession } = authSlice.actions
export default authSlice.reducer
